import { IStylesRule } from 'Shared/backend/styleUtils';
import { BorderFields } from 'Shared/backend/styles/Borders';
import { MarginFields } from 'Shared/backend/styles/Margins';
import { PaddingFields } from 'Shared/backend/styles/Padding';
import { BrightnessFields } from 'Shared/themes/Brightness';
import { ColourFields } from 'Shared/themes/Colour';
import { FontWeightFields } from 'Shared/themes/FontWeight';
import { FontSizeFields } from 'Shared/themes/FontSize';
import { LetterSpacingFields } from 'Shared/themes/LetterSpacing';
import { LineHeightFields } from 'Shared/themes/LineHeight';
import { TextShadowFields, textShadowInitial } from 'Shared/themes/TextShadow';
import { FontFamilyFields } from 'Shared/themes/FontFamily';
import { TextTransformFields } from 'Shared/themes/TextTransform';
import { TextAlignFields } from 'Shared/themes/TextAlign';
import { TextDecorationFields } from 'Shared/themes/TextDecoration';
import { WidthFields } from 'Shared/themes/Width';
import { HeightFields } from 'Shared/themes/Height';
import { CornerRadiusFields } from 'Shared/themes/CornerRadius';
import { ScrollbarColourFields } from 'Shared/themes/ScrollbarColour';
import { GridGapFields } from 'Shared/themes/GridGap';
import { BannerLogoFields } from 'Shared/themes/BannerLogo';
import { MenuTitleColourFields } from 'Shared/themes/MenuTitleColour';
import { MenuButtonColoursFields } from 'Shared/themes/MenuButtonColours';
import { colourRule, resolveColour, resolvedBorder } from 'Shared/model/settingColour';
import { resolveFontSize } from 'Shared/model/settingText';
import { CustomStyleRule, LocationPathStep, Palette } from 'Shared/model/venueSettings';
import { escapeStyleClass, pathLocation } from 'Shared/frontend/widgets/locationMatches';


/*
	One css property a Style Builder rule can set - what it does, how it is edited, and the rule
	fields it contributes.

	'fields' is a rule minus its location: the Style Builder's rules carry their own (see
	customStyleRule), so nothing here names where it applies. That is the one real difference from
	the entries in tweaksStyle.ts, which this catalogue is otherwise copied from. The two are kept
	apart on purpose - the code-defined styles and the ones a venue builds should be free to diverge.

	'ui' edits 'params', which is what gets handed to the field functions. 'initial' seeds params
	when the property is first chosen.
*/
export type RuleProperty = {
	ui?: any,
	initial?: any,
	fields: Omit<IStylesRule,'location'>,
};


/*
	'palette' is the venue's, handed in rather than read off the theme context: these are built while
	siteTheme() is still being assembled, so there is no theme to read yet - and on the settings page
	and the Style Builder there is none at all.
*/
export function ruleProperties(palette:Palette): {[name: string]: RuleProperty}
{
	return {
		Colour: {
			ui: ColourFields,

			fields: {
				css: (params = {}) => colourRule('color', params.colour, palette),
			}
		},

		'Background colour': {
			ui: ColourFields,

			fields: {
				css: (params = {}) => colourRule('background', params.colour, palette),
			}
		},

		/* An svg icon takes its colour from 'fill', not 'color' - for the icon beside a piece of nav
		   text (the mobile nav's 'Icon' / 'Current page icon' parts). */
		'Icon colour': {
			ui: ColourFields,

			fields: {
				css: (params = {}) => colourRule('fill', params.colour, palette),
			}
		},

		'Margins': {

			ui: MarginFields,

			initial: {
				margin: { sides: 'different' }
			},

			fields: {
				margin: (params = {}) => params?.margin,
			}
		},

		'Padding': {

			ui: PaddingFields,

			initial: {
				padding: { sides: 'different' }
			},

			fields: {
				padding: (params = {}) => params?.padding,
			}
		},

		/* A border colour can name a palette entry like any other colour here, and is resolved on the
		   way out: addBorders() builds a css shorthand out of it and has no palette to resolve
		   against. */
		'Borders': {

			ui: BorderFields,

			initial: {
				border: { sides: 'all',all:{ style:'solid',colour:'gray',width:'2px' }}
			},

			fields: {
				border: (params = {}) => resolvedBorder(params?.border,palette),
			}
		},

		/* Both this and the 'Tweaks' category's Brightness let the filter land wherever it is
		   pointed - here via the rule's own location, there via an 'Apply to' selector. filter
		   applies to the whole subtree, so it brightens everything below the target too. */
		Brightness: {
			ui: BrightnessFields,

			fields: {
				css: (params) => ({
					filter: `brightness(${params.brightness}%)`
				}),
			}
		},

		'Centre object': {
			fields: {
				css: () => ({
					height: '100%',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				})
			}
		},

		'Left align': {
			fields: {
				css: () => ({
					textAlign: 'left'
				})
			}
		},

		/* 'Left align' with the other two alignments available - what a venue reaches for to put the
		   centring back on something the code left aligns, the event page's headings being the one
		   that does. */
		'Text align': {
			ui: TextAlignFields,

			fields: {
				css: (params = {}) => params.align ? {textAlign: params.align} : {},
			}
		},

		'Font weight': {
			ui: FontWeightFields,

			fields: {
				css: (params = {}) => params.weight ? {fontWeight: params.weight} : {},
			}
		},

		'Font size': {
			ui: FontSizeFields,

			fields: {
				css: (params = {}) => params.size ? {fontSize: resolveFontSize(params.size)} : {},
			}
		},

		'Letter spacing': {
			ui: LetterSpacingFields,

			fields: {
				css: (params = {}) => params.spacing != undefined ? {letterSpacing: params.spacing} : {},
			}
		},

		'Line height': {
			ui: LineHeightFields,

			fields: {
				css: (params = {}) => params.lineHeight != undefined ? {lineHeight: params.lineHeight} : {},
			}
		},

		'Text shadow': {
			ui: TextShadowFields,

			initial: textShadowInitial,

			fields: {
				css: (params = {}) => textShadowRule(params,palette),
			}
		},

		'Font family': {
			ui: FontFamilyFields,

			fields: {
				css: (params = {}) => params.family ? {fontFamily: params.family} : {},
			}
		},

		'Text transform': {
			ui: TextTransformFields,

			fields: {
				css: (params = {}) => params.transform ? {textTransform: params.transform} : {},
			}
		},

		'Text decoration': {
			ui: TextDecorationFields,

			fields: {
				css: (params = {}) => params.decoration ? {textDecoration: params.decoration} : {},
			}
		},

		Width: {
			ui: WidthFields,

			fields: {
				css: (params = {}) => params.width == 'full' ? {width: '100%'} : {},
			}
		},

		Height: {
			ui: HeightFields,

			fields: {
				css: (params = {}) => params.height ? {height: params.height} : {},
			}
		},

		'Corner radius': {
			ui: CornerRadiusFields,

			fields: {
				css: (params = {}) => params.radius ? {borderRadius: params.radius} : {},
			}
		},

		/* The scrollbar on a sideways-scrolling row of posters or menu items - Content.tsx spreads a
		   'Venue styles' tag's css over its 'black #666363' fallback. '!important' is carried over
		   from the per-venue code entries this replaced. */
		'Scrollbar colour': {
			ui: ScrollbarColourFields,

			fields: {
				css: (params = {}) => scrollbarColourRule(params.handle, params.track, palette),
			}
		},

		/* Both gaps of a grid widget in one property - a grid with no tag keeps Grid.tsx's 3vw / 4vw
		   default, and an axis left blank here keeps it too. */
		'Grid gap': {
			ui: GridGapFields,

			fields: {
				css: (params = {}) => gridGapRule(params.columnGap, params.rowGap),
			}
		},

		/* The fluid side padding and fixed height a home-page banner logo image sits in - the
		   treatment The Drummer / Showcase 3 used to carry as a per-venue code entry. Those two set it
		   from the tweaksStyle.ts copy of this property, on the banner image node itself
		   (seedBannerLogoStyles.ts); this one is here for a venue that would rather name the treatment
		   as a style of its own. */
		'Banner logo': {
			ui: BannerLogoFields,

			fields: {
				css: (params = {}) => bannerLogoRule(params.padding, params.height),
			}
		},

		/* The section headings and item names of a menu. A rule at the menu page colours the lot; a
		   rule on a section class colours that kind of section instead, overriding the page one for
		   everything under it. */
		'Menu title colour': {
			ui: MenuTitleColourFields,

			fields: {
				css: (params = {}) => menuTitleColourRule(params.colour, palette),
			}
		},

		/* The Food / Drinks buttons of a menu's sticky button row. The row is reached from the menu
		   page: a button carries its kind as a plain css class rather than on the style path. */
		'Menu button colours': {
			ui: MenuButtonColoursFields,

			fields: {
				css: (params = {}) => menuButtonColoursRule(params.food, params.drinks, palette),
			}
		},
	};
}


/*
	The colour travels down as a custom property rather than as a plain 'color' declaration. Nested
	sections generate selectors of equal specificity, so which one won would otherwise depend on the
	order Emotion happened to insert them in. Custom properties inherit, so the innermost section
	always wins.
*/
function menuTitleColourRule(colour:any, palette:Palette): object
{
	const resolved = resolveColour(colour, palette);
	if (resolved == '')
		return {};

	return {
		'--menu-title-colour': resolved,
		'.title': {
			color: 'var(--menu-title-colour)'
		}
	};
}


/* The Food / Drinks button backgrounds, each dropped when unset. A Plain button is cleared back to
   whatever sits behind the row: it is the 'no colour' one of the three, and the sub-nav button
   styling underneath would otherwise fill it like any other. */
function menuButtonColoursRule(food:any, drinks:any, palette:Palette): object
{
	const resolvedFood = resolveColour(food, palette);
	const resolvedDrinks = resolveColour(drinks, palette);

	if (resolvedFood == '' && resolvedDrinks == '')
		return {};

	const ret: any = {'button.plain': {backgroundColor: 'inherit'}};
	if (resolvedFood != '')
		ret['button.food'] = {backgroundColor: resolvedFood};
	if (resolvedDrinks != '')
		ret['button.drinks'] = {backgroundColor: resolvedDrinks};
	return ret;
}


/* 'scrollbar-color: <handle> <track>', or {} when neither is set. A half-set pair falls back to the
   Content.tsx scrollbar colours for the missing side, since 'scrollbar-color' can't mix a colour
   with 'auto'. */
function scrollbarColourRule(handle:any, track:any, palette:Palette): object
{
	const resolvedHandle = resolveColour(handle, palette);
	const resolvedTrack = resolveColour(track, palette);

	if (resolvedHandle == '' && resolvedTrack == '')
		return {};

	return {scrollbarColor: `${resolvedHandle || 'black'} ${resolvedTrack || '#666363'} !important`};
}


/* The column-gap / row-gap pair, each dropped when its box is empty. '0' is a real value, so test
   for an empty string rather than falsiness. */
function gridGapRule(columnGap:any, rowGap:any): object
{
	const ret: any = {};
	if (columnGap != undefined && columnGap != '')
		ret.columnGap = columnGap;
	if (rowGap != undefined && rowGap != '')
		ret.rowGap = rowGap;
	return ret;
}


/* The banner logo's left/right padding and height, each dropped when unset so a rule that sets only
   one still works. resolveFontSize() turns the Amount control's fixed-or-fluid value into a css
   length, the same way the 'Font size' property does. */
function bannerLogoRule(padding:any, height:any): object
{
	const ret: any = {};
	if (padding != undefined && padding != '') {
		const value = resolveFontSize(padding);
		ret.paddingLeft = value;
		ret.paddingRight = value;
	}
	if (height != undefined && height != '')
		ret.height = height;
	return ret;
}


/* '0 0 5px black' from the four stored parts, or {} when nothing has been set. A number renders as
   px; the colour resolves a palette link or literal, falling back to black. */
function textShadowRule(params:any, palette:Palette): object
{
	if (params.x == undefined && params.y == undefined && params.blur == undefined && !params.colour)
		return {};

	const colour = resolveColour(params.colour, palette) || 'black';
	return {textShadow: `${params.x ?? 0}px ${params.y ?? 0}px ${params.blur ?? 0}px ${colour}`};
}


/*
	The location expression a rule's location maps onto. Copied from selectLocation() in
	tweaksStyle.ts, without its undefined case - a stored rule always names one, and extended with
	the class match the code-defined styles have no equivalent of.

	'class' reaches the styled component itself as well as everything under it: the '**' lets the
	class land at any depth from where the style was applied downwards, and the class-filtered '*'
	is the node it actually lands on. 'aClass' is empty only for a rule stored before the class was
	picked, which venueCustomStyles() drops rather than passing here - an empty class filter matches
	every component.

	'path' is the general case, spelled out step by step by the venue rather than picked whole - see
	pathLocation(), which builds it.
*/
export function ruleLocation(location:CustomStyleRule['location'], aClass:string,
	path:LocationPathStep[]): string[]
{
	switch (location) {
		case 'self':  return ['*'];
		case 'class': return ['**',`*:${escapeStyleClass(aClass)}`];
		case 'path':  return pathLocation(path);
	}
}


/*
	Where a set of rules is being written, which decides both the classes the location picker offers
	and what a class means.

	'selector' and 'styleBuilder' are the two ordinary style contexts - a class there is a component
	on the style path, matched by ruleLocation() - and they differ only in whether the embedded-html
	elements are offered alongside. The rest are the settings page's own rule lists, where a class
	names one part of a fixed piece of chrome and resolves to a css selector instead; see
	nestedSelector().
*/
export type ClassContext = 'selector' | 'styleBuilder' | 'navDesktop' | 'navMobile' | 'navMore'
	| 'footer' | 'event';

/*
	The elements inside a block of embedded / user-entered html - a Text widget's text, an event's
	Details, a staff resource - that a Style Builder rule can dress, and the label each is offered
	under.

	These are location classes like any other as far as the rule is concerned, but they name no
	component: nothing hands them down the style path, because the html is set as innerHTML and has
	no components under it at all. So instead of resolving to a path match, a rule on one of them
	lands on the styled component itself with its css nested under the selector, and ordinary css
	descendant matching finds the elements - see buildRule() in venueCustomStyles.ts. That also means
	they are Style Builder only: the standard styles' 'Apply to' picker resolves a class by path and
	has no way to use one.

	LocationBuilder reads this.
*/
export const embeddedElements: {element:string, label:string}[] = [
	{element:'strong', label:'Bold text'},
];

/*
	The parts of a piece of chrome that the settings page lets a venue point a rule at - the 'Apply
	to' options each of its rule lists offers, and the keys nestedSelector() knows.

	These are location classes that name no component either, for a plainer reason than the embedded
	html: the nav renders outside any page. So they are reached by css selector instead. The parts are
	spelled out per node rather than shared because the same name means a different element in each - a
	Mobile button is not a Desktop one - and because a node should offer only what it has.

	LocationBuilder and locationClasses.ts read this.
*/
export const settingsParts: {[context: string]: {part:string, label:string}[]} = {
	navDesktop: [
		{part:'bar',          label:'Bar background'},
		{part:'button',       label:'Button'},
		{part:'button-hover', label:'Button (hover)'},
		{part:'current',      label:'Current page button'},
		{part:'logo',         label:'Logo'},
		{part:'dropdown',     label:'Drop-down panel'},
	],
	navMobile: [
		{part:'bar',          label:'Bar background'},
		{part:'button',       label:'Button'},
		{part:'button-hover', label:'Button (hover)'},
		{part:'current',      label:'Current page button'},
		{part:'icon',         label:'Button icon'},
		{part:'current-icon', label:'Current page button icon'},
	],
	navMore: [
		{part:'bar',          label:'Panel background'},
		{part:'button',       label:'Button'},
		{part:'button-hover', label:'Button (hover)'},
		{part:'current',      label:'Current page button'},
		{part:'icon',         label:'Icons'},
		{part:'address',      label:'Address'},
		{part:'phone',        label:'Phone number'},
	],
	footer: [
		{part:'bar',          label:'Divider & padding'},
		{part:'heading',      label:'Venue name'},
	],
	event: [
		{part:'heading',      label:'Date & name headings'},
	],
};


/*
	The nested selector a class points at, relative to the element the rule's css lands on: the nav
	wrapper ShowWrapElement styles, or the component an embedded-html rule sits on.

	'' means that element rather than something under it - the props sit at the top of the rule's css
	rather than under a selector. That covers every nav's 'bar' part (the 'More' menu's included - its
	background is on the wrapper, see BaseSettings mobile.overlay) and every class in the two ordinary
	style contexts, where a class is a component on the style path and ruleLocation() resolves it
	instead.

	The button class differs per nav node (.nav-button / .nav-mobile-button / .nav-more-button); the
	state is a pseudo-class or the .active class the nav components already set. A class that is not
	one of the context's parts also returns '' - see settingRulesCss(), which drops such a rule rather
	than letting it dress the whole node.
*/
export function nestedSelector(context:ClassContext, aClass:string): string
{
	if (context == 'styleBuilder') {
		const entry = embeddedElements.find(e => e.element == aClass);
		return entry ? `.embedded-html ${entry.element}` : '';
	}

	if (context == 'selector')
		return '';

	/* The footer's own <footer> element carries the rule's flat css (divider + padding); the venue
	   name is the .footer-venue-name <h4> under it (see BaseFooter.tsx). */
	if (context == 'footer')
		return aClass == 'heading' ? '& .footer-venue-name' : '';

	/* The event page's date and name headings - the rule's own location (['^','*:title']) already
	   lands it on the <h3>s, so nothing is nested under. */
	if (context == 'event')
		return '';

	/* The 'More' full-screen menu's non-button parts sit inside the .nav-more-panel div (NavMore.tsx):
	   its icons are every <svg> in there (the nav labels have none - these are the phone / address /
	   social icons), and the phone number and street address carry their own class / element. 'bar'
	   falls through to '' - the menu's background is on the wrapper (BaseSettings mobile.overlay). */
	if (context == 'navMore')
		switch (aClass) {
			case 'icon':    return '& .nav-more-panel svg';
			case 'address': return '& .nav-more-panel address';
			case 'phone':   return '& .nav-more-panel .nav-more-phone';
		}

	const button = context=='navDesktop' ? '.nav-button'
		: context=='navMobile' ? '.nav-mobile-button'
		: '.nav-more-button';

	switch (aClass) {
		case 'button':       return `& ${button}`;
		case 'button-hover': return `& ${button}:hover`;
		case 'current':      return `& ${button}.active`;
		case 'logo':         return '& .nav-logo';
		case 'dropdown':     return '& .subnav > div';
		case 'icon':         return `& ${button} svg`;
		case 'current-icon': return `& ${button}.active svg`;
		default:             return '';
	}
}

